// External imports
import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { ThemeProvider, StyledEngineProvider, adaptV4Theme } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import withStyles from '@mui/styles/withStyles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';

// Internal imports
import * as authHttp from '../http/auth';
import * as examsHttp from '../http/exams';
import * as clinicsHttp from '../http/clinics';
import * as utils from '../utils';
import * as userutils from '../userutils';

const styles = theme => ({
  root: {
    width: '100%',
  },
  table: {
    minWidth: 700,
  },
  tableHead: {
    fontWeight: 'bold',
  },
  tableContainer: {
    height: 320,
  },
  margin: {
    margin: theme.spacing(1),
    marginLeft: 'auto',
  },
  examRow: {
    padding: 0,
  },
  autoUpdateText: {
    padding: '5px',
  },
  firefoxSpecialAlign: {
    position: 'relative',
    top: '-6px',
  },
});

const compactRowTheme = createTheme(
  adaptV4Theme({
    overrides: {
      MuiTableCell: {
        root: {
          padding: '20px',
          fontSize: '12px',
        },
      },
    },
  })
);

const mapStateToProps = state => ({
  exams: state.exams.list,
  examsCount: state.exams.count,
  exam: state.exams.current,
  searchingExams: state.exams.searchingExams,
  search: state.exams.search,
  clinics: state.clinics.list,
  clinics_map: state.clinics.clinics_map,
  users: state.clinics.users,
  categories: state.clinics.categories,
  user: state.auth.currentUser,
});

const DEFAULT_ROWS_PER_PAGE = 20;

const ExamsToReview = props => {
  const {
    classes = {},
    exams = [],
    examsCount = 0,
    user = null,
    clinics_map = {},
    users = [],
    history,
  } = props;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  // Build filter object from state
  const filterFromState = useCallback(() => {
    if (!user) return null;
    return {
      medic: user.id,
      status: 'digitado',
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      ordering: 'study_datetime',
      // only take exams from the last 30 days
      studyDatetimeFrom: utils.dateToString(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)), // 30 days ago
    };
  }, [rowsPerPage, page, user]);

  // Search exams to review
  const doSearch = useCallback(() => {
    const filters = filterFromState();
    if (!filters) return;
    examsHttp.listExamsToReview(filters);
  }, [filterFromState]);

  useEffect(() => {
    authHttp.getCurrentUser();
    clinicsHttp.list();
    examsHttp.listExamsToReview(filterFromState());
    return () => {
      examsHttp.clearAll();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    doSearch();
  }, [page, rowsPerPage, user, doSearch]);

  // Show exam details
  const showExam = useCallback(
    (examId, event) => {
      event.stopPropagation();
      history.push(`/exams/exam/${examId}`);
    },
    [history]
  );

  // Get clinic by ID
  const getClinic = useCallback(
    clinicId => clinics_map?.[clinicId] || {},
    [clinics_map]
  );

  // Get user name by ID
  const getUserName = useCallback(
    userId => userutils.getUserName(userId, users),
    [users]
  );

  const tableHeaders = [
    { label: 'Paciente', field: 'patient_name' },
    { label: 'Data Exame', field: 'study_datetime' },
    { label: 'Mod', align: 'left' },
    { label: 'Clínica', field: 'clinic_name', align: 'left' },
    { label: 'Descrição', field: 'description', align: 'left' },
    { label: 'Médico', align: 'left' },
  ];

  return (
    <Fragment>
      <div className={classes.tableContainer}>
        <Paper className={classes.root}>
          <Table className={classes.table}>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={compactRowTheme}>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    {tableHeaders.map(h => (
                      <TableCell align={h.align} key={h.label} className={classes.tableHead}>
                        {h.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exams.map(e => (
                    <TableRow key={e.id} hover className={classes.examRow}>
                      <TableCell onClick={event => showExam(e.id, event)} component="th" scope="row">
                        {e.patient_name}
                      </TableCell>
                      <TableCell onClick={event => showExam(e.id, event)} align="left">
                        {utils.formatDate(e.study_datetime)}
                      </TableCell>
                      <TableCell onClick={event => showExam(e.id, event)} align="left">
                        {e.modality}
                      </TableCell>
                      <TableCell onClick={event => showExam(e.id, event)} align="left">
                        {getClinic(e.clinic_id).name}
                      </TableCell>
                      <TableCell onClick={event => showExam(e.id, event)} align="left">
                        {e.description}
                      </TableCell>
                      <TableCell onClick={event => showExam(e.id, event)} align="left">
                        {e.report ? getUserName(e.report.medic) : ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ThemeProvider>
            </StyledEngineProvider>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            component="div"
            count={examsCount}
            rowsPerPage={rowsPerPage}
            labelRowsPerPage="Itens por página"
            page={page}
            backIconButtonProps={{ 'aria-label': 'Página Anterior' }}
            nextIconButtonProps={{ 'aria-label': 'Próxima Página' }}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={event => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      </div>
    </Fragment>
  );
};

ExamsToReview.propTypes = {
  classes: PropTypes.object.isRequired,
  exams: PropTypes.array,
  examsCount: PropTypes.number,
  user: PropTypes.object,
  clinics_map: PropTypes.object,
  users: PropTypes.array,
  history: PropTypes.object.isRequired,
};

export default withRouter(withStyles(styles)(connect(mapStateToProps)(ExamsToReview)));
